import { useEffect, useState } from 'react';
import { NavOverlay } from '@/components/NavOverlay';
import { supabase } from '@/integrations/supabase/client';
import { trackContactClick } from '@/hooks/useTrackPageView';
import { useResolveUserId } from '@/hooks/useResolveUserId';
import { usePageTitle } from '@/hooks/usePageTitle';
import SiteFooterLinks from '@/components/site/SiteFooterLinks';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';

interface BioBlock {
  id: string;
  type: 'text' | 'rich-text' | 'photo' | 'separator';
  content: string;
  image_url: string;
  width: string;
  order: number;
}

interface ExternalLinkData {
  id: string;
  network: string;
  url: string;
  order: number;
}

interface AboutData {
  photo_url: string | null;
  bio_blocks: BioBlock[];
  email: string;
  phone: string;
  location: string;
  links: ExternalLinkData[];
}

interface SiteStyle {
  text_color: string;
  link_color: string;
  font_family: string;
  font_size: number;
  font_weight: number;
  custom_font_url: string | null;
  dark_mode_enabled: boolean;
  dark_bg_color: string;
  dark_text_color: string;
  dark_link_color: string;
  footer_links: unknown;
}

interface FooterLink {
  label: string;
  url: string;
}

export default function AboutPage() {
  const { userId } = useResolveUserId();
  const [about, setAbout] = useState<AboutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [style, setStyle] = useState<SiteStyle | null>(null);
  const [footerLinks, setFooterLinks] = useState<FooterLink[]>([]);

  usePageTitle('About');

  useEffect(() => {
    if (!userId) return;

    const load = async () => {
      const [{ data: aboutRows }, { data: styleData }] = await Promise.all([
        supabase.rpc('get_published_about', { p_user_id: userId }),
        supabase.from('site_settings').select('text_color, link_color, font_family, font_size, font_weight, custom_font_url, dark_mode_enabled, dark_bg_color, dark_text_color, dark_link_color, footer_links').eq('user_id', userId).limit(1).maybeSingle(),
      ]);
      const aboutData = Array.isArray(aboutRows) ? aboutRows[0] : aboutRows;

      if (aboutData) {
        setAbout({
          photo_url: aboutData.photo_url,
          bio_blocks: (aboutData.bio_blocks as any[] || []).sort((a: any, b: any) => a.order - b.order),
          email: aboutData.email || '',
          phone: aboutData.phone || '',
          location: aboutData.location || '',
          links: (aboutData.links as any[] || []).sort((a: any, b: any) => a.order - b.order),
        });
      }
      if (styleData) {
        setStyle(styleData as SiteStyle);
        const rawLinks = (styleData as any).footer_links;
        setFooterLinks(Array.isArray(rawLinks) ? (rawLinks as FooterLink[]) : []);
      }
      setLoading(false);
    };
    load();
  }, [userId]);

  const isDark = style?.dark_mode_enabled;
  const bgColor = isDark ? (style?.dark_bg_color || '#1a1a1a') : '#F7F6F2';
  const textColor = isDark ? (style?.dark_text_color || '#F7F6F2') : (style?.text_color || '#000000');
  const linkColor = isDark ? (style?.dark_link_color || '#6699ff') : (style?.link_color || '#0000FF');
  const fontFamily = style?.font_family || 'IBM Plex Mono';

  const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('55') && digits.length >= 12) {
      return `https://wa.me/${digits}`;
    }
    return `tel:${phone}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NavOverlay />
      </div>
    );
  }

  if (!about) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor }}>
        <NavOverlay />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, color: textColor }}>
      <NavOverlay />
      <div style={{
        maxWidth: 600, margin: '0 auto', padding: '120px 24px 80px',
        fontFamily: `'${fontFamily}', 'Courier New', monospace`,
        fontSize: style?.font_size || 16, fontWeight: style?.font_weight || 300,
      }}>
        {about.photo_url && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img src={about.photo_url} alt="" className="about-photo" style={{ objectFit: 'cover' }} />
          </div>
        )}

        {about.bio_blocks.length > 0 && (
          <div style={{ marginTop: 48 }}>
            {about.bio_blocks.map(block => (
              <div key={block.id} style={{ marginBottom: 24 }}>
                {block.type === 'text' && (
                  <p style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{block.content}</p>
                )}
                {block.type === 'rich-text' && (
                  <div style={{ lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.content || '', { FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'], FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'] }) }} className="about-rich-text" />
                )}
                {block.type === 'photo' && block.image_url && (
                  <img src={block.image_url} alt="" style={{ width: `${block.width}%`, maxWidth: '100%' }} />
                )}
                {block.type === 'separator' && (
                  <hr style={{ border: 'none', borderTop: `1px solid ${textColor}20`, margin: '24px 0' }} />
                )}
              </div>
            ))}
          </div>
        )}

        {(about.email || about.phone || about.location) && (
          <div style={{ marginTop: 48, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {about.email && (
              <a href={`mailto:${about.email}`}
                onClick={() => userId && trackContactClick(userId, 'email')}
                style={{ display: 'flex', alignItems: 'center', gap: 8, color: textColor, textDecoration: 'none' }}>
                <Mail size={14} strokeWidth={1} /> {about.email}
              </a>
            )}
            {about.phone && (
              <a href={formatPhone(about.phone)} target="_blank" rel="noopener noreferrer"
                onClick={() => userId && trackContactClick(userId, 'phone')}
                style={{ display: 'flex', alignItems: 'center', gap: 8, color: textColor, textDecoration: 'none' }}>
                <Phone size={14} strokeWidth={1} /> {about.phone}
              </a>
            )}
            {about.location && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapPin size={14} strokeWidth={1} /> {about.location}
              </span>
            )}
          </div>
        )}

        {about.links.length > 0 && (
          <div style={{ marginTop: 40, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            {about.links.map(link => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, color: textColor,
                  textDecoration: 'none', transition: 'color 0.2s ease', fontSize: 14,
                }}
                onMouseEnter={e => (e.currentTarget.style.color = linkColor)}
                onMouseLeave={e => (e.currentTarget.style.color = textColor)}
              >
                <ExternalLink size={14} strokeWidth={1} />
                {link.network}
              </a>
            ))}
          </div>
        )}
      </div>

      <SiteFooterLinks links={footerLinks} textColor={textColor} fontFamily={fontFamily} />

      <style>{`
        .about-photo { width: 240px; height: auto; }
        @media (max-width: 640px) { .about-photo { width: 160px; } }
        .about-rich-text a { color: ${linkColor}; }
      `}</style>
    </div>
  );
}
